import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { T } from "@/lib/testIds";

export default function Login() {
  const { login, loginRedirectPath } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await login(email, password);
      toast.success("Welcome back");
      const to = loc.state?.from?.pathname || loginRedirectPath(r.user);
      nav(to, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="/login-hero.png"
          alt="CollectOne — reminders and collection"
          className="w-full h-full object-cover"
          style={{ filter: "brightness(1.25) saturate(1.05)" }}
        />
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="co-label" style={{ color: "rgba(255,255,255,0.75)" }}>
            Welcome to CollectOne
          </div>
          <div className="font-heading text-3xl font-bold tracking-tight mt-3 max-w-md">
            One platform for every collection.
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="max-w-md w-full">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <img src="/collectone-logo.webp" alt="CollectOne" className="w-8 h-8 object-contain" />
            <span className="font-heading font-bold tracking-tight">CollectOne</span>
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-[#52525B]">
            Welcome back — enter your workspace.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label className="co-label">Email</Label>
              <Input
                data-testid={T.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11"
                placeholder="you@business.com"
              />
            </div>
            <div>
              <Label className="co-label">Password</Label>
              <Input
                data-testid={T.loginPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              data-testid={T.loginSubmit}
              disabled={busy}
              className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white font-semibold py-3 rounded-sm co-btn-press transition-colors duration-200 disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#52525B]">
            New to CollectOne?{" "}
            <Link to="/forgot-password" className="text-xs text-[#4338CA] font-semibold hover:text-[#3730A3]" data-testid="login-forgot-link">
              Forgot password?
            </Link>
            <Link to="/signup" className="text-[#4338CA] font-semibold hover:text-[#3730A3]">
              Create a workspace
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
